import { Box, Button, Tooltip, useTheme } from '@mui/material';
import { Candle2, Edit } from 'iconsax-react';
import { type Dispatch, type FC, type SetStateAction, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { UserEmailSignature } from '@core/types/api/UserEmailSignature/UserEmailSignature';
import { hasValue } from '@core/utils/equality';

type CustomToolbarProps = {
  onSignatureSelect: (content: UserEmailSignature) => void;
  signatures?: UserEmailSignature[];
  signaturesPage?: number;
  setSignaturesPage?: Dispatch<SetStateAction<number>>;
  signaturesTotalPages?: number;
};

const SignaturesDropdown: FC<CustomToolbarProps> = ({
  onSignatureSelect,
  signatures,
  signaturesPage = 0,
  setSignaturesPage,
  signaturesTotalPages = 0,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const theme = useTheme();

  const handleOptionClick = (option: UserEmailSignature): void => {
    onSignatureSelect(option);
    setIsOpen(false);
  };

  const navigate = useNavigate();

  return (
    <Box sx={{ paddingTop: '5px', position: 'relative', display: 'inline-block' }}>
      <Button
        onClick={() => {
          setIsOpen(!isOpen);
          // Reset to first page when opening dropdown
          if (!isOpen && hasValue(setSignaturesPage)) {
            setSignaturesPage(0);
          }
        }}
        sx={{
          minWidth: '28px',
          width: '28px',
          height: '24px',
          padding: 0,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '3px',
          '&:hover': {
            backgroundColor: '#f0f0f0',
          },
          '&:focus': {
            outline: '1px solid #007bff',
            backgroundColor: '#f0f0f0',
          },
        }}
      >
        <Edit size={16} color='#444' />
      </Button>

      {isOpen && hasValue(signatures) && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 1000,
            minWidth: '200px',
            border: '1px solid #ccc',
            borderRadius: '6px',
            backgroundColor: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            marginTop: '4px',
            overflow: 'hidden',
          }}
        >
          {signatures.map((option) => (
            <Box
              key={option.id}
              onClick={() => handleOptionClick(option)}
              sx={{
                padding: '12px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                borderBottom: '1px solid #f0f0f0',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: '#f8f9fa',
                },
                '&:last-child': {
                  borderBottom: 'none',
                },
              }}
            >
              {option.name}
            </Box>
          ))}
          <Box
            key='mange-signatures'
            onClick={() => navigate('/emailconfig')}
            sx={{
              padding: '12px 16px',
              cursor: 'pointer',
              fontSize: '14px',
              borderBottom: '1px solid #f0f0f0',
              color: theme.palette.SlabBlue['600'],
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: '#f8f9fa',
              },
              '&:last-child': {
                borderBottom: 'none',
              },
            }}
          >
            <Candle2
              size={16}
              color={theme.palette.SlabBlue['600']}
              style={{ marginRight: '8px', paddingTop: '5px' }}
            />
            Manage signatures
          </Box>
          {signaturesTotalPages > 1 && (
            <Box
              sx={{
                padding: '8px 16px',
                borderTop: '1px solid #f0f0f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '12px',
                color: theme.palette.text.secondary,
              }}
            >
              <Button
                size='small'
                disabled={signaturesPage === 0}
                onClick={() => setSignaturesPage?.(signaturesPage - 1)}
                sx={{ minWidth: '60px', fontSize: '11px' }}
              >
                Previous
              </Button>
              <Box>
                {signaturesPage + 1} / {signaturesTotalPages}
              </Box>
              <Button
                size='small'
                disabled={signaturesPage >= signaturesTotalPages - 1}
                onClick={() => setSignaturesPage?.(signaturesPage + 1)}
                sx={{ minWidth: '60px', fontSize: '11px' }}
              >
                Next
              </Button>
            </Box>
          )}
        </Box>
      )}

      {isOpen && (
        <Box
          onClick={() => setIsOpen(false)}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
          }}
        />
      )}
    </Box>
  );
};

export const CustomToolbar: FC<CustomToolbarProps> = ({
  onSignatureSelect,
  signatures,
  signaturesPage,
  setSignaturesPage,
  signaturesTotalPages,
}) => (
  <Box id='toolbar' sx={{ display: 'flex', alignItems: 'center', gap: 1, padding: '8px' }}>
    <Box className='ql-formats'>
      <select className='ql-header' defaultValue=''>
        <option value='1'>Heading 1</option>
        <option value='2'>Heading 2</option>
        <option value=''>Normal</option>
      </select>
    </Box>
    <Box className='ql-formats'>
      <button className='ql-bold' aria-label='Bold' />
      <button className='ql-italic' aria-label='Italic' />
      <button className='ql-underline' aria-label='Underline' />
    </Box>
    <Box className='ql-formats'>
      <button className='ql-list' value='ordered' aria-label='Numbered list' />
      <button className='ql-list' value='bullet' aria-label='Bulleted list' />
    </Box>
    <Tooltip title='Email signature'>
      <Box className='ql-formats'>
        <SignaturesDropdown
          onSignatureSelect={onSignatureSelect}
          signatures={signatures ?? []}
          signaturesPage={signaturesPage}
          setSignaturesPage={setSignaturesPage}
          signaturesTotalPages={signaturesTotalPages}
        />
      </Box>
    </Tooltip>
    <Box className='ql-formats'>
      <button className='ql-link' aria-label='Insert link' />
      <button className='ql-image' aria-label='Insert image' />
    </Box>
    <Box className='ql-formats'>
      <button className='ql-clean' aria-label='Remove formatting' />
    </Box>
  </Box>
);
